import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import axios from "@utils/axios";
import { ChargingTelemetry } from "@/types/chargingTelemetry";

export interface ChargingTelemetryParams {
  evseID: string;
  sessionID: string;
}

const fetchChargingTelemetry = async (params: ChargingTelemetryParams, signal?: AbortSignal): Promise<ChargingTelemetry[]> => {
  const response = await axios.get(`/api/evse/${params.evseID}/session/${params.sessionID}/telemetry/`, { signal });
  const data = response.data as ChargingTelemetry[];
  // console.log("data", data);
  data.sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp));
  // console.log("sorted", data);
  return data;
};

type QueryKeyType = readonly ["charging", "telemetry", ChargingTelemetryParams | "disabled"];

export const chargingTelemetryKeys = {
  all: ["charging"] as const,
  telemetries: () => [...chargingTelemetryKeys.all, "telemetry"] as const,

  telemetry: (params: ChargingTelemetryParams | "disabled") => [...chargingTelemetryKeys.telemetries(), params] as const
};

export const useChargingTelemetry = (
  params: ChargingTelemetryParams | null,
  options?: Omit<UseQueryOptions<ChargingTelemetry[], Error, ChargingTelemetry[], QueryKeyType>, "queryKey" | "queryFn">
) => {
  // console.log("params", params);
  const isEnabled = params !== null && !!params.evseID && !!params.sessionID;

  // console.log("useChargingTelemetr",params?.evseID, params?.sessionID)

  return useQuery({
    queryKey: params ? chargingTelemetryKeys.telemetry(params) : chargingTelemetryKeys.telemetry("disabled"),
    queryFn: ({ signal }) => {
      if (!params) {
        throw new Error("!params are required");
      }
      return fetchChargingTelemetry(params, signal);
    },
    staleTime: 20 * 1000, // 20 seconds
    cacheTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: 30000, // refetch every 30 seconds ?
    enabled: isEnabled,
    ...options
  });
};
